/* A viewpoint's loaded graph turned into the shape the 3D network views draw.
 *
 * Much less work than the XYZ charts' equivalent, because this view needs no fields. A chart has to
 * find numeric fields, scope them to the plotted types, resolve a selection and exclude every
 * component missing a value; a network view draws the graph it was handed. What it needs instead is
 * the two things the charts throw away: the references, and the parent/child structure.
 *
 * What is shared with the charts is the part that would be wrong to write twice - labels through
 * `getLabelsInterfaceForEnhancedScopeData`, and colors through `createScopeDataReader`, so
 * conditional formatting wins over the component type's color here exactly as it does there.
 */

import type {
  APIReferenceAttributes,
  ArdoqId,
  FormattingFilter,
} from '@ardoq/api-types';
import { toNetworkMarker } from './toNetworkMarker';
import { APIEntityType, LineType, ViewIds } from '@ardoq/api-types';
import type { EnhancedScopeData } from '@ardoq/data-model';
import type { GroupingRule } from '@ardoq/api-types';
import { uniq } from 'lodash';
import { groupingRuleOperations } from '@ardoq/core';
import type { FormattingAndLabelRules } from '@ardoq/data-model';
import {
  enhancedScopeDataOperations,
  referenceUtils,
  type FilterColorData,
} from '@ardoq/scope-data';
import { ARDOQ_DEFAULT_LOCALE, type Locale } from '@ardoq/locale';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale } from '@ardoq/i18n';
import type {
  LegendComponentType,
  LegendReferenceType,
  RawGraphNode2,
} from '@ardoq/graph';
import {
  buildGraphFromTraversalAndSearch,
  getLegendComponentTypeColor,
  getGraphInterfaceForEnhancedScopeData,
  getLabelsInterfaceForEnhancedScopeData,
  getLabelWithFormattingForViewsNotSupportingMultilabeling,
  type LoadedGraphWithViewpointMode,
} from '@ardoq/graph';
import type {
  NetworkEdge,
  NetworkFolded,
  NetworkGraph,
  NetworkLineStyle,
  NetworkNode,
} from '@ardoq/xyz-charts';
import { NO_MARKER, toModalFill } from '@ardoq/xyz-charts';
import type { PermissionContext } from '@ardoq/access-control';
import {
  numericFieldsInScope,
  readFieldValueFromScopeData,
} from 'streams/fields/numericFieldsInScope';
import type { FieldWithPermissions } from 'streams/fields/getFieldsForViewpoint';
import {
  getActiveConditionalFormattingForLegendPure,
  type ActiveConditionalFormattingForLegend,
} from '@ardoq/view-legend';
import { createScopeDataReader } from 'tabview/xyzCharts/sourceData/readers';
import {
  toNodeSizing,
  toNodeWeight,
  type Network3DNodeSizeBy,
} from './toNodeSizing';
import {
  toDrawnEndpoint,
  toGroupCollapse,
  type FlattenedGroupItem,
} from './collapseGroups';

/**
 * The grouped graph, when the reader has asked for groups.
 *
 * Built through the app's own grouping engine rather than reimplemented. `buildGroups` and its
 * grouper registry are where every rule type lives, and it works off scope data via
 * `getGraphInterfaceForEnhancedScopeData` - so this view can group without reaching for the model
 * interfaces it deliberately avoided.
 *
 * The result arrives in *graph item* space rather than component space, which is the part that
 * matters. A grouping can clone one component into several groups, so a graph item id is the
 * identity here and `referenceList` is already expressed in those ids. Mapping the references back
 * to component ids would be the bug: a cloned component's references would all land on whichever
 * copy happened to be found first.
 */
const toGroupedGraphItems = ({
  loadedGraph,
  enhancedScopeData,
  groupingRules,
  formattingAndLabelRules,
  i18nLocale,
}: {
  loadedGraph: LoadedGraphWithViewpointMode;
  enhancedScopeData: EnhancedScopeData;
  groupingRules: GroupingRule[];
  formattingAndLabelRules: FormattingAndLabelRules;
  i18nLocale: I18nLocale;
}) => {
  const graphInterface = getGraphInterfaceForEnhancedScopeData({
    enhancedScopeData,
    conditionalFormatting: formattingAndLabelRules.rules,
    groupingRules,
    i18nLocale,
  });

  return buildGraphFromTraversalAndSearch({
    /**
     * The viewpoint's own graph, not the global `graphModel$`.
     *
     * `buildGraphFromTraversalAndSearch` opens with `isGraphModelValid`, which requires every
     * reference in the graph and both its endpoints to be present in the scope data behind
     * `graphInterface` - and on failure it logs and returns an empty result, which this builder
     * reports as "no components to show" for a viewpoint that has components. Two independently
     * debounced streams cannot be relied on to agree across a viewpoint change, so the check was
     * one race away from an empty grouped view. A graph model that is merely behind rather than
     * invalid is quieter and no better: the components are drawn with references missing.
     *
     * Taking it off the scope data removes the disagreement rather than guarding against it - it
     * is the graph of exactly the data `graphInterface` was built from. Both viewpoint-mode
     * siblings do the same; neither subscribes `graphModel$` at all.
     *
     * It also stops the view re-solving on changes it cannot see. `graphModel$` re-emits for any
     * change to the loaded components, references, models or scenario ids, and each new object
     * rebuilt the graph and re-ran the force layout for an identical picture.
     */
    graph: enhancedScopeData.graphData,
    groupingRules,
    viewId: ViewIds.NETWORK_3D,
    loadedGraph,
    graphInterface,
    i18nLocale,
  });
};

/** Every item of the group forest, with the id of the item that contains it. */
const toFlattenedGroupItems = (
  rootGroups: RawGraphNode2[]
): FlattenedGroupItem[] => {
  const flattened: FlattenedGroupItem[] = [];
  const stack = [...rootGroups]
    .reverse()
    .map(item => ({ item, parentId: null as string | null }));
  const seen = new Set<string>();

  while (stack.length > 0) {
    const entry = stack.pop();
    if (!entry || seen.has(entry.item.id)) continue;
    seen.add(entry.item.id);
    flattened.push(entry);

    const children = entry.item.children ?? [];
    for (let at = children.length - 1; at >= 0; at--) {
      stack.push({ item: children[at], parentId: entry.item.id });
    }
  }

  return flattened;
};

/** Matching the component reader's, so a reference and a component resolve their color alike. */
const REFERENCE_COLOR_OPTIONS = { useAsBackgroundStyle: false };

/**
 * The host's line type as the package's own.
 *
 * A mapping rather than a cast, because the package deliberately does not depend on `LineType` -
 * and an exhaustive one, so a fifth line type added to the model is a type error here rather than
 * a reference that silently comes out solid.
 */
const toNetworkLineStyle = (line: LineType | undefined): NetworkLineStyle => {
  switch (line) {
    case LineType.DASHED:
      return 'dashed';
    case LineType.DOTTED:
      return 'dotted';
    case LineType.SOLID:
    case undefined:
      return 'solid';
  }
};

/**
 * Why the view has nothing to draw.
 *
 * Only one reason, unlike the charts' five. A network view with components and no references still
 * draws - the hierarchy layouts are entirely about containment, and even the force layout has
 * something to say about an unconnected cloud. Components are the only thing it cannot do without.
 */
export type Network3DEmptyReason = 'no components' | null;

export type Network3DSourceDataArgs = {
  loadedGraph: LoadedGraphWithViewpointMode;
  i18nLocale: I18nLocale;
  locale: Locale;
  formattingAndLabelRules: FormattingAndLabelRules;
  /** Threaded rather than read off the singleton cache, for the reason `XyzPointsArgs` gives. */
  filterColorData: FilterColorData;
  /** The user's grouping rules, which only matter when there is at least one. */
  groupingRules: GroupingRule[];
  /**
   * What the reader chose to encode as mark size, and the field it names when that is a field.
   *
   * The one pair of settings this builder takes, and the only settings that can change the graph
   * rather than only how it is drawn: size arrives as `NetworkNode.weight`, which is also what the
   * grouped layout reserves space from. Everything else the view exposes is resolved downstream.
   */
  nodeSizeBy: Network3DNodeSizeBy;
  sizeByFieldName: string;
  /** For resolving a chosen field against what the reader is actually allowed to see. */
  permissionContext: PermissionContext;
  /**
   * The groups the reader has closed, which only the grouped path can act on.
   *
   * Data rather than styling, and that is why it is here rather than downstream: a closed group
   * is a smaller graph, so the layout is solved without what it hides and the references that
   * crossed its boundary land on the group instead.
   */
  collapsedGroupIds: readonly string[];
};

/**
 * What the app's own view legend needs, collected where the types are already resolved.
 *
 * Off the loaded scope rather than through `getComponentTypesForLegend`, which reads the global
 * component interface: this view only ever runs in viewpoint mode, where the components are the
 * traversal's and not the workspace's. Gathering both lists inside the loops that already ask for
 * each type costs nothing and cannot disagree with what was drawn - a legend built from a second
 * source is how one ends up naming a type the picture does not contain.
 */
export type Network3DLegendData = {
  componentTypes: LegendComponentType[];
  referenceTypes: LegendReferenceType[];
  /**
   * The rows naming the colors conditional formatting produced.
   *
   * Without these the formatting is on screen and unexplained, which is half of why the type
   * swatch must not borrow a formatted color: between them the reader would have a red row
   * labelled with a type name and nothing saying what red means.
   */
  activeConditionalFormatting: ActiveConditionalFormattingForLegend;
};

export type Network3DSourceData = {
  viewInstanceId: string;
  graph: NetworkGraph;
  legend: Network3DLegendData;
  /**
   * Whether the graph's hierarchy is the reader's grouping rather than containment.
   *
   * Carried rather than inferred from the nodes, because the two are indistinguishable downstream:
   * a weightless node with children is a group here and a component with a zero degree and a child
   * there. The view needs to know because only one layout can express a grouping.
   */
  isGrouped: boolean;
  /**
   * Whether there is anything to collapse, which is what the two header buttons gate on.
   *
   * Not the same question as `isGrouped`. A grouping rule that resolves to one group per
   * component produces a forest of childless roots, which is a grouping with no group in it - and
   * a Collapse all that does nothing is worse than one that is greyed out, because the reader
   * concludes the view is broken rather than that there is nothing to do.
   */
  hasGroups: boolean;
  /**
   * Every group that could be closed, open or not, by mark id.
   *
   * Carried so a double-click can write the setting. Closing one group is `[...stored, id]`, but
   * *opening* one out of a stored wildcard is "every group except this one" - which only the full
   * list can say. Empty on the ungrouped path.
   */
  collapsibleGroupIds: readonly string[];
  /**
   * What each closed group is hiding, by the group's mark id.
   *
   * Built here rather than in the view because it is the same derivation the drawn graph comes
   * from: the members are already resolved, their references are already matched to their styles,
   * and re-deriving either downstream is how the thumbnail ends up showing a different estate
   * from the one that was folded away.
   *
   * One entry per closed group, including the ones with no picture to offer - see
   * `MAXIMUM_THUMBNAIL_MARKS`. Empty for an ungrouped viewpoint and for one with nothing closed.
   */
  foldedGroups: ReadonlyMap<string, NetworkFolded>;
  /**
   * The numeric fields the size picker offers, from the same scope the sizing was resolved against.
   *
   * Carried on the source data for the reason `XyzPoints.numericFields` is: the settings bar has to
   * show the list the derivation actually used, or a reader picks a field the picture ignores.
   */
  numericFields: FieldWithPermissions[];
  /**
   * The components on screen, by component id, deduplicated.
   *
   * Carried rather than derived from `graph.nodes`, because a node's `id` is the *mark's* id. In
   * the grouped path those differ in both directions: a group's id names no component, and a
   * cloned component's marks carry clone ids while the component's own id appears on none of
   * them. Read as component ids they made "Create scenario from view" count groups, handed the
   * dialog ids that resolve to nothing, and told the rendered-components check that a cloned
   * component was not drawn.
   */
  componentIds: ArdoqId[];
  /**
   * The reference behind each drawn edge, parallel to `graph.edges`, `null` where there is none.
   *
   * Carried rather than derived, for the reason `NetworkLayout.edgeIndices` is carried: a
   * consumer that wants to act on the reference behind an edge cannot get at it from the edge,
   * which says how a reference is *drawn* and nothing about what it is. Written in the same step
   * that pushes the edge, so the two cannot drift.
   *
   * `null` for the grouped path's untyped edges, which name no reference in `referencesById`.
   */
  referenceIds: readonly (ArdoqId | null)[];
  /** References dropped for naming a component outside the loaded scope. */
  droppedReferenceCount: number;
  isViewEmpty: boolean;
  emptyReason: Network3DEmptyReason;
  locale: Locale;
  i18nLocale: I18nLocale;
};

const EMPTY_GRAPH: NetworkGraph = { nodes: [], edges: [] };

/** A result with nothing drawn, which the empty paths fill in the reason on. */
const emptyNetwork3DSourceData = (
  viewInstanceId: string
): Network3DSourceData => ({
  viewInstanceId,
  graph: EMPTY_GRAPH,
  legend: {
    componentTypes: [],
    referenceTypes: [],
    activeConditionalFormatting: { components: [], references: [], tags: [] },
  },
  numericFields: [],
  componentIds: [],
  isGrouped: false,
  hasGroups: false,
  collapsibleGroupIds: [],
  foldedGroups: new Map(),
  referenceIds: [],
  droppedReferenceCount: 0,
  isViewEmpty: true,
  emptyReason: null,
  locale: ARDOQ_DEFAULT_LOCALE,
  i18nLocale: DEFAULT_ARDOQ_I18NLOCALE,
});

/**
 * The most marks a thumbnail is built for.
 *
 * Not a performance ceiling, though it is also that. A force solve of a few hundred nodes drawn
 * into two hundred pixels is a grey disc: every mark is sub-pixel, every reference crosses every
 * other, and the picture answers none of the questions - chain, star or mesh - that a thumbnail
 * is there to answer. Past this the member count on its own is the more honest answer, and the
 * tooltip falls back to it.
 */
const MAXIMUM_THUMBNAIL_MARKS = 120;

/**
 * What each closed group is hiding: how much, and the picture where there is one worth drawing.
 *
 * One entry per closed group rather than one per group that earned a thumbnail, because the two
 * readings come apart at the cap: a group holding four hundred components has no picture a
 * tooltip can show and is precisely the one whose count a reader most wants.
 *
 * Walked over `items` rather than over `collapsedIds`, for the group item itself: a closed group
 * that is also a component accounts for one more mark than it hides - see `NetworkFolded`.
 *
 * The graph is flat and uniformly weighted, deliberately on both counts. Flat because the nesting
 * inside a closed group is scaffolding the reader closed away - re-drawing it would put
 * weightless nodes and regions into a two-hundred-pixel box - and uniform because a thumbnail is
 * read for its shape rather than for a quantity: sized by a field, most of a subgraph is
 * unmeasured and comes out as identical dots around one large one, which says less than nothing.
 *
 * Groups behind the closed one are in neither the count nor the picture. They carry no component,
 * so they are neither marks a thumbnail could show nor components the count is about.
 */
const toFoldedGroups = ({
  items,
  collapse,
  internalEdges,
  toComponentFields,
  toDrawnComponentId,
}: {
  items: readonly FlattenedGroupItem[];
  collapse: ReturnType<typeof toGroupCollapse>;
  internalEdges: ReadonlyMap<string, NetworkEdge[]>;
  toComponentFields: (
    componentId: ArdoqId
  ) => Pick<NetworkNode, 'label' | 'typeName' | 'fill'>;
  /** Shared with the node mapping, so the thumbnail draws what the picture would have. */
  toDrawnComponentId: (item: RawGraphNode2) => ArdoqId | null;
}): ReadonlyMap<string, NetworkFolded> => {
  const folded = new Map<string, NetworkFolded>();

  for (const { item } of items) {
    if (!collapse.collapsedIds.has(item.id)) continue;
    const groupId = item.id;
    /* A closed group that is also a component stands for itself as well as for what it hides -
       `PARENT` and `PARENT_ALL` group by components. One a rule invented stands only for what it
       hides, because there is no component there to count. */
    const isComponent = toDrawnComponentId(item) !== null;
    const nodes: NetworkNode[] = (
      collapse.membersOf.get(groupId) ?? []
    ).flatMap(member => {
      const componentId = toDrawnComponentId(member);
      return componentId === null
        ? []
        : [
            {
              id: member.id,
              matchKey: member.id,
              parentId: null,
              weight: 1,
              ...toComponentFields(componentId),
            },
          ];
    });

    const counts = {
      foldedCount: nodes.length,
      accountsFor: nodes.length + (isComponent ? 1 : 0),
    };

    if (nodes.length === 0 || nodes.length > MAXIMUM_THUMBNAIL_MARKS) {
      folded.set(groupId, { ...counts, graph: null });
      continue;
    }

    /* Against the marks that survived rather than against the members: a reference to one of the
       groups this skipped would name a node the thumbnail does not contain, and `toLinks` would
       drop it - silently, and after the layout had already reserved space for it. */
    const drawable = new Set(nodes.map(({ id }) => id));

    folded.set(groupId, {
      ...counts,
      graph: {
        nodes,
        edges: (internalEdges.get(groupId) ?? []).filter(
          ({ sourceId, targetId }) =>
            drawable.has(sourceId) && drawable.has(targetId)
        ),
      },
    });
  }

  return folded;
};

/**
 * The grouped graph, in the package's own node and edge shapes.
 *
 * Identity here is the *graph item* id rather than the component id, and that is the whole reason
 * this path exists rather than a flag on the flat one. A grouping can clone one component into
 * several groups, so a component id no longer names one mark - and `referenceList` already speaks
 * graph-item ids, so an edge lands on the copy it belongs to. Keying on component ids would put
 * every reference of a cloned component on whichever copy was built first, which draws as a
 * plausible picture of a coupling that is not there.
 *
 * Group nodes carry `weight: 0`, which the renderer draws as nothing: an expanded group is a
 * region, and the region is the layout's business.
 */
const toGroupedSourceData = ({
  viewInstanceId,
  loadedGraph,
  enhancedScopeData,
  groupingRules,
  formattingAndLabelRules,
  i18nLocale,
  locale,
  toReferenceStyle,
  toComponentFields,
  toWeight,
  collapsedGroupIds,
}: {
  viewInstanceId: string;
  loadedGraph: LoadedGraphWithViewpointMode;
  enhancedScopeData: EnhancedScopeData;
  groupingRules: GroupingRule[];
  formattingAndLabelRules: FormattingAndLabelRules;
  i18nLocale: I18nLocale;
  locale: Locale;
  toReferenceStyle: (
    referenceId: ArdoqId,
    reference: APIReferenceAttributes
  ) => Pick<
    NetworkEdge,
    'typeName' | 'stroke' | 'lineStyle' | 'markerAtSource' | 'markerAtTarget'
  >;
  toComponentFields: (
    componentId: ArdoqId
  ) => Pick<NetworkNode, 'label' | 'typeName' | 'fill'>;
  /** Resolved once by the caller, so both paths size marks by the same answer. */
  toWeight: (modelId: ArdoqId | null, degree: number) => number | null;
  /** The groups the reader has closed, as the view setting stores them. */
  collapsedGroupIds: readonly string[];
}): Omit<Network3DSourceData, 'legend' | 'numericFields'> => {
  const { rootGroups, referenceList } = toGroupedGraphItems({
    loadedGraph,
    enhancedScopeData,
    groupingRules,
    formattingAndLabelRules,
    i18nLocale,
  });

  const items = toFlattenedGroupItems(rootGroups);
  const drawable = new Set(items.map(({ item }) => item.id));
  const collapse = toGroupCollapse(items, collapsedGroupIds);

  /**
   * The component a graph item stands for, or `null` where it stands for a group.
   *
   * Not `modelId !== undefined`, which is what this was and which is wrong for most of the rules
   * on offer. A `modelId` is whatever the grouper keyed on: `groupByComponentType` writes the
   * *component type's* id, `groupByWorkspace` the workspace's, `groupByField` the field's. Only
   * `PARENT` and `PARENT_ALL` write a component id, because only those group by a component -
   * `setParentsAndAddGroupIfNeeded` writes the ancestor's own `modelId`, and consolidation means
   * that component never also appears as a leaf.
   *
   * So the absence of a `modelId` says "a group the engine invented", and its presence says
   * nothing at all. Read as a component id, a type group was classified as a mark: it took a
   * weight, so a sphere was drawn at the empty centre of its own region; `getLabel` of a type id
   * is blank, so the sphere and its tooltip were nameless; and the type id went into
   * `componentIds`, which is what "Create scenario from components in view" is built from.
   *
   * Asking the scope data settles it, and does so for every rule at once including the ones
   * nobody has written yet.
   */
  const toDrawnComponentId = ({ modelId }: RawGraphNode2): ArdoqId | null =>
    modelId !== undefined &&
    enhancedScopeData.componentsById[modelId] !== undefined
      ? modelId
      : null;

  const degrees = new Map<string, number>();
  const edges: NetworkEdge[] = [];
  /* Pushed beside `edges`, never derived from it - see `Network3DSourceData.referenceIds`. */
  const referenceIds: (ArdoqId | null)[] = [];
  /* The references that fell *inside* a closed group, kept for that group's thumbnail. Collected
     in the loop that drops them rather than by a second pass: the pass that decides a reference
     is internal is the pass that has already resolved its style. */
  const internalEdges = new Map<string, NetworkEdge[]>();

  /**
   * Counted off the loaded scope, not off what reached the grouping engine.
   *
   * By the time `referenceList` exists, `getTraversalResultForViewpointMode` has already dropped
   * every reference with an endpoint outside `scopeComponentIds` - the same predicate the
   * ungrouped path applies itself, only applied upstream and silently. So counting misses here
   * counted nothing, and turning on any grouping rule made the "N references are not shown"
   * notice vanish while the same N references stayed missing. Worse than a lost statistic: the
   * notice lives in a `role="status"` region, so a screen reader announced that the problem had
   * gone away.
   */
  const inScope = new Set(loadedGraph.scopeComponentIds);
  const droppedReferenceCount = loadedGraph.scopeReferenceIds.filter(
    referenceId => {
      const reference = enhancedScopeData.referencesById[referenceId];
      return (
        !reference ||
        !inScope.has(reference.source) ||
        !inScope.has(reference.target)
      );
    }
  ).length;

  for (const edgeItem of referenceList) {
    const { sourceId, targetId, modelId, isParentChildReference } = edgeItem;
    /**
     * Containment is structure, not a reference, and `referenceList` carries both.
     *
     * `getTraversalResultForViewpointMode` appends one synthetic entry per parent/child pair, and
     * `getResultingReferenceList` passes it through with a `modelId` of `parentId-childId` - which
     * is not in `referencesById`, so it resolved to no type, no color and no line style and drew
     * as a plain grey line indistinguishable from a real untyped reference, with no legend row to
     * decode it. Worse than a cosmetic problem: it asserted a reference where the model has
     * containment, `degrees` counted it so **size by References** counted children, and
     * `graph.edges.length` counted it so the canvas announced a reference count that was not one.
     *
     * The ungrouped path never saw these - it reads `scopeReferenceIds` - so turning on any
     * grouping rule silently added edges. `toContainmentLinks` in the package already treats
     * containment as a spring rather than a route; the hierarchy carries it here too, through
     * `parentId` below.
     */
    if (isParentChildReference) continue;
    /* Not counted as dropped: these are graph-item ids the grouping engine produced, so an
       endpoint missing from the item tree is this view failing to draw something rather than the
       viewpoint excluding it, and the notice is about the latter. */
    if (
      sourceId === undefined ||
      targetId === undefined ||
      !drawable.has(sourceId) ||
      !drawable.has(targetId)
    ) {
      continue;
    }

    /* Undrawable for the reason the ungrouped loop gives, and reached more often here: a grouper
       that maps both endpoints onto one item turns a perfectly ordinary reference into a loop. */
    if (sourceId === targetId) continue;

    const reference =
      modelId === undefined
        ? undefined
        : enhancedScopeData.referencesById[modelId];
    const style =
      reference && modelId
        ? toReferenceStyle(modelId, reference)
        : {
            typeName: '',
            stroke: null,
            lineStyle: 'solid' as const,
            markerAtSource: NO_MARKER,
            markerAtTarget: NO_MARKER,
          };

    /* Both ends lifted to whatever is on screen for them, which for an endpoint inside a closed
       group is that group. Done before the degrees are counted rather than after, so a closed
       group's reference count is the count of the references actually drawn on it - the same
       number its tooltip reports and the same number **size by References** sizes it by. */
    const drawnSource = toDrawnEndpoint(collapse, sourceId);
    const drawnTarget = toDrawnEndpoint(collapse, targetId);

    if (drawnSource === drawnTarget) {
      /* Inside one closed group. Not a reference the picture can show - there is one mark where
         both of its ends are - but it is exactly what the thumbnail exists to show, so it is kept
         against the group rather than dropped. Only when both ends are genuinely folded: a
         grouper mapping two components onto one *open* item produces the same equality and has no
         group to keep it against. */
      const folded = collapse.foldedInto.get(sourceId);
      if (
        folded !== undefined &&
        folded === collapse.foldedInto.get(targetId)
      ) {
        const kept = internalEdges.get(folded);
        const edge = { sourceId, targetId, ...style };
        if (kept) {
          kept.push(edge);
        } else {
          internalEdges.set(folded, [edge]);
        }
      }
      continue;
    }

    edges.push({ sourceId: drawnSource, targetId: drawnTarget, ...style });
    referenceIds.push(modelId ?? null);

    degrees.set(drawnSource, (degrees.get(drawnSource) ?? 0) + 1);
    degrees.set(drawnTarget, (degrees.get(drawnTarget) ?? 0) + 1);
  }

  /* Everything a closed group is hiding is off the graph entirely, rather than drawn at its
     group's position or drawn transparent. A collapse is a reader saying "not now" about a part
     of the estate, and the layout has to be solved without it for the rest to have the room. */
  const drawn = items.filter(({ item }) => !collapse.foldedInto.has(item.id));

  /**
   * The color a closed group wears: whichever one most of the marks behind it wear.
   *
   * Through the same `toModalFill` the open region votes with, rather than a second count of the
   * same thing. The promise is that closing a group does not change what colour that part of the
   * estate is - before this it did, since a closed group has no component behind it and fell
   * through to a categorical slot, turning a cloud of purple applications into a green sphere -
   * and a promise that two implementations agree is a promise until one of them changes its
   * tie-break.
   */
  const toClosedGroupFill = (groupId: string): string | null =>
    toModalFill(
      (collapse.membersOf.get(groupId) ?? []).map(member => {
        const componentId = toDrawnComponentId(member);
        return componentId === null
          ? null
          : toComponentFields(componentId).fill;
      })
    );

  const nodes: NetworkNode[] = drawn.map(({ item, parentId }) => {
    const componentId = toDrawnComponentId(item);
    const isClosed = collapse.collapsedIds.has(item.id);
    const isGroup = componentId === null;
    const componentFields =
      componentId === null ? null : toComponentFields(componentId);

    return {
      id: item.id,
      /**
       * The component behind the mark, so a re-arrangement can recognise it.
       *
       * A grouping renames every mark when the rules change - `item.id` encodes the path through
       * the groups - so matching on the mark's own id would match nothing across a rule change and
       * the animation would be an explosion. The component id survives, and a group that has no
       * component behind it has no identity beyond this arrangement.
       *
       * A cloned component gives several marks the same key, which is the intended split: every
       * copy starts from the one place the original was.
       */
      matchKey: componentId ?? item.id,
      label: componentFields?.label ?? item.label ?? item.name ?? '',
      typeName: componentFields?.typeName ?? '',
      fill:
        componentFields?.fill ?? (isClosed ? toClosedGroupFill(item.id) : null),
      parentId,
      /* Zero for an *open* group, so it is a region rather than a mark - and the renderer relies
         on that, drawing a weightless node as nothing. A closed one is the opposite case: its
         contents are not on screen, so there is no region for it to be, and it is drawn as the
         mark that stands for them. A component is sized the way the ungrouped path sizes it, off
         the graph-item id its references were counted against.

         A closed group is sized by its own drawn degree rather than by an aggregate of what it
         hides, which keeps the three readings of it in agreement: the sphere, the reference count
         in its tooltip and the references drawn on it are all the same number. An aggregate would
         make the mark larger than anything its tooltip could explain - and under **Uniform** or
         a numeric field there is no aggregate to take that means what the axis means. How much is
         behind it is answered by the member count and the thumbnail, which is what those are
         for. */
      weight:
        isGroup && !isClosed
          ? 0
          : toWeight(componentId, degrees.get(item.id) ?? 0),
    };
  });

  return {
    viewInstanceId,
    graph: { nodes, edges },
    isGrouped: true,
    referenceIds,
    hasGroups: collapse.hasGroups,
    collapsibleGroupIds: collapse.collapsibleIds,
    foldedGroups: toFoldedGroups({
      items,
      collapse,
      internalEdges,
      toComponentFields,
      toDrawnComponentId,
    }),
    /* Off the resolved component rather than off the marks: a group has no component behind it,
       and a cloned component has several marks that all stand for the one id. Through
       `toDrawnComponentId` rather than off `modelId` directly, because a `modelId` is not always
       a component - see there. */
    componentIds: uniq(
      items.flatMap(({ item }) => toDrawnComponentId(item) ?? [])
    ),
    droppedReferenceCount,
    isViewEmpty: nodes.length === 0,
    emptyReason: nodes.length === 0 ? 'no components' : null,
    locale,
    i18nLocale,
  };
};

export const buildNetwork3DSourceData = (
  viewInstanceId: string,
  {
    loadedGraph,
    i18nLocale,
    locale,
    formattingAndLabelRules,
    filterColorData,
    groupingRules,
    nodeSizeBy,
    sizeByFieldName,
    permissionContext,
    collapsedGroupIds,
  }: Network3DSourceDataArgs
): Network3DSourceData => {
  const enhancedScopeData = loadedGraph.scopeData;
  const componentIds = loadedGraph.scopeComponentIds;
  /* Offered and resolved before anything is built, because the resolution can reject the setting -
     a field that has left scope falls back to the reference count - and both paths have to size
     from the same answer the settings bar will be showing. */
  const numericFields = numericFieldsInScope(
    enhancedScopeData,
    permissionContext
  );
  const sizing = toNodeSizing(nodeSizeBy, sizeByFieldName, numericFields);
  /* The same rules the marks are colored by, named. Off the loaded scope rather than the global
     model interfaces, as the Blocks view does it - this view only ever runs in viewpoint mode. */
  const activeConditionalFormatting =
    getActiveConditionalFormattingForLegendPure(
      enhancedScopeData,
      /* Narrowed to the rules that carry a color, which is what a formatting rule is and what the
       helper keeps anyway - the list also holds label rules, which have nothing to show here. */
      formattingAndLabelRules.rules.filter(
        (rule): rule is FormattingFilter =>
          'color' in rule && Boolean(rule.color)
      ),
      i18nLocale,
      true
    );
  const readFieldValue = readFieldValueFromScopeData(enhancedScopeData);
  const toWeight = (modelId: ArdoqId | null, degree: number): number | null =>
    toNodeWeight(sizing, {
      degree,
      fieldValue:
        sizing.kind === 'field' && modelId !== null
          ? readFieldValue(modelId, sizing.fieldName)
          : null,
    });
  /* Only the rules that are actually usable. A half-finished rule in the panel is not a grouping,
     and grouping by nothing would rebuild the graph into one root per component - the same picture
     as ungrouped, arrived at the expensive way. */
  const activeGroupingRules =
    groupingRuleOperations.getValidRules(groupingRules);
  const isGrouped = activeGroupingRules.length > 0;

  if (componentIds.length === 0) {
    return {
      ...emptyNetwork3DSourceData(viewInstanceId),
      numericFields,
      locale,
      i18nLocale,
      emptyReason: 'no components',
    };
  }

  const labelsInterface = getLabelsInterfaceForEnhancedScopeData({
    enhancedScopeData,
    formattingFilters: formattingAndLabelRules.rules,
    i18nLocale,
  });

  const labels = new Map<ArdoqId, string>(
    componentIds.map(componentId => [
      componentId,
      getLabelWithFormattingForViewsNotSupportingMultilabeling({
        modelId: componentId,
        entityLabel: enhancedScopeData.componentsById[componentId]?.name ?? '',
        entityType: APIEntityType.COMPONENT,
        i18nLocale,
        referenceLabelFilters: [],
        labelsInterface,
      }),
    ])
  );

  const reader = createScopeDataReader({
    enhancedScopeData,
    filterColorData,
    labels,
  });

  /* Membership is asked often enough - once per reference end and once per parent - that a Set is
     worth building rather than scanning the id list. */
  const inScope = new Set(componentIds);

  /**
   * Degree per component, counted before the nodes are built because it sizes them.
   *
   * Counted over the references that survive scoping rather than all of them, so a node's size
   * agrees with the edges actually drawn on it. A component whose only reference points outside
   * the viewpoint reads as unconnected, which is what the picture shows.
   */
  const degrees = new Map<ArdoqId, number>();
  const edges: NetworkEdge[] = [];
  /* Pushed beside `edges`, never derived from it - see `Network3DSourceData.referenceIds`. */
  const referenceIds: (ArdoqId | null)[] = [];
  let droppedReferenceCount = 0;
  /* Keyed by name, which is what the legend shows. Two workspaces can define a type of the same
     name and the legend has one row for it either way. */
  const referenceTypesByName = new Map<string, LegendReferenceType>();
  const componentTypesByName = new Map<string, LegendComponentType>();

  /**
   * A reference's drawn style, and its legend row, from one place.
   *
   * Shared by the grouped and ungrouped paths deliberately. Two copies of this is how a legend ends
   * up describing a colour the picture does not use - which has happened twice in this view
   * already, once for component types and once for the edge opacity.
   */
  const toReferenceStyle = (
    referenceId: ArdoqId,
    reference: APIReferenceAttributes
  ) => {
    /* The null-safe overload, which takes the reference rather than its id: a collapsed-path
       reference lives in the virtual model and is not in `referencesById` by its own id. */
    const referenceType = enhancedScopeDataOperations.getReferenceType(
      enhancedScopeData,
      reference
    );

    if (referenceType) {
      const existing = referenceTypesByName.get(referenceType.name);
      const globalTypeId = referenceUtils.toGlobalReferenceTypeId(reference);
      if (existing) {
        if (!existing.globalTypeIds.includes(globalTypeId)) {
          existing.globalTypeIds.push(globalTypeId);
        }
      } else {
        referenceTypesByName.set(referenceType.name, {
          ...referenceType,
          globalTypeIds: [globalTypeId],
          /* Empty because this legend is not selectable. The class exists so a selectable legend
             can highlight the references of a type in the DOM, and there is no DOM to highlight
             here - the references are pixels in a canvas. */
          className: '',
        });
      }
    }

    return {
      typeName: referenceType?.name ?? '',
      /* The reference's own colour, resolved the way every other view resolves one, so conditional
         formatting wins over the reference type exactly as it does for a component. The resolver
         needs an id and a collapsed-path reference has none in `referencesById`, so the type's
         colour is the fallback - and `null` past that, which the package reads as "nothing
         configured one" rather than as black. */
      stroke:
        referenceUtils.getReferenceDisplayColorAsSVGAttributes(
          enhancedScopeData,
          filterColorData,
          referenceId,
          REFERENCE_COLOR_OPTIONS
        ).stroke ??
        referenceType?.color ??
        null,
      lineStyle: toNetworkLineStyle(referenceType?.line),
      /* By the field each came from rather than by the spelling of its value: a reference type may
         hold a `*Start` value in either field, and the field is what says which end it decorates.
         A UML composition is a filled diamond at the source and nothing at the target, so reading
         one alone says the opposite of what the reference means. */
      markerAtSource: toNetworkMarker(referenceType?.lineBeginning),
      markerAtTarget: toNetworkMarker(referenceType?.lineEnding),
    };
  };

  /**
   * A component's label, type and colour, and its legend row, from one place.
   *
   * Shared with the grouped path for the reason `toReferenceStyle` is: the legend is built from
   * what this records, so a second copy is a second answer.
   */
  const toComponentFields = (componentId: ArdoqId) => {
    const componentType =
      enhancedScopeData.componentsById[componentId] !== undefined
        ? enhancedScopeDataOperations.getComponentTypeByComponentId(
            enhancedScopeData,
            componentId
          )
        : null;

    if (componentType) {
      const existing = componentTypesByName.get(componentType.name);
      if (existing) {
        existing.ids.push(componentId);
      } else {
        componentTypesByName.set(componentType.name, {
          name: componentType.name,
          icon: componentType.icon,
          image: componentType.image,
          ids: [componentId],
          /**
           * The type's own color, not the first member's drawn one.
           *
           * It was `reader.getColors(componentId).fill`, which resolves conditional formatting -
           * and this entry is written on whichever component of the type is reached first. So one
           * formatted component gave its color to the whole row: a rule matching 3 of 200
           * applications made the Application swatch red while 197 were drawn blue, and
           * reordering the traversal changed the row with nothing about the picture changing.
           *
           * `getComponentTypesForLegend` keys on the color *and* the name for exactly this
           * reason, one type appearing in several. Keyed on the name alone the only correct
           * answer is the type's, and the colors formatting actually produced belong in the
           * formatting rows beside it.
           */
          backgroundColor: getLegendComponentTypeColor(componentType),
        });
      }
    }

    return {
      label: reader.getLabel(componentId),
      /* The type's name rather than its id: it is what the legend shows and what the layer planes
         are keyed on, and two workspaces sharing a type name should share a plane and a color. */
      typeName: componentType?.name ?? '',
      /* `getColors` has already resolved conditional formatting over the type color. `undefined`
         from the reader becomes `null` here, which is the package's "no configured color" and is
         distinct from a component that is genuinely black. */
      fill: reader.getColors(componentId).fill ?? null,
    };
  };

  if (isGrouped) {
    const grouped = toGroupedSourceData({
      viewInstanceId,
      loadedGraph,
      enhancedScopeData,
      groupingRules: activeGroupingRules,
      formattingAndLabelRules,
      i18nLocale,
      locale,
      toReferenceStyle,
      toComponentFields,
      toWeight,
      collapsedGroupIds,
    });

    return {
      ...grouped,
      numericFields,
      /* Read after the grouped build rather than before it: the two resolvers record into these
         maps as they go, so the legend lists the types that were actually drawn. */
      legend: {
        componentTypes: [...componentTypesByName.values()],
        referenceTypes: [...referenceTypesByName.values()],
        activeConditionalFormatting,
      },
    };
  }

  for (const referenceId of loadedGraph.scopeReferenceIds) {
    const reference = enhancedScopeData.referencesById[referenceId];
    if (!reference) {
      droppedReferenceCount += 1;
      continue;
    }

    const { source, target } = reference;
    if (!inScope.has(source) || !inScope.has(target)) {
      droppedReferenceCount += 1;
      continue;
    }

    /**
     * A self-reference is a supported model state that this view cannot draw.
     *
     * `toLinks` drops `source === target` for a reason of its own - a spring of length zero
     * divides by its own distance - so it reaches neither `links` nor `routes`, and counting it
     * here made every reader of the count disagree with the picture: `degrees` incremented twice
     * on the same id, so size by References counted one self-reference as two; the aria label
     * announced a reference nobody can find; and `toReferenceStyle` gave its type a legend row for
     * a line that is nowhere on screen.
     *
     * Not counted as dropped either. That notice is about a reference whose other end is outside
     * the viewpoint, which a reader can act on by widening the traversal; this one is inside it.
     */
    if (source === target) continue;

    edges.push({
      sourceId: source,
      targetId: target,
      ...toReferenceStyle(referenceId, reference),
    });
    referenceIds.push(referenceId);

    degrees.set(source, (degrees.get(source) ?? 0) + 1);
    degrees.set(target, (degrees.get(target) ?? 0) + 1);
  }

  const nodes: NetworkNode[] = componentIds.map(componentId => {
    const parent =
      enhancedScopeData.componentsById[componentId]?.parent ?? null;

    return {
      id: componentId,
      matchKey: componentId,
      ...toComponentFields(componentId),
      /* Only kept when the parent is itself in the viewpoint. A traversal can load a child without
         its parent, and a parent id pointing outside the scope would make the node a root of a
         tree whose apex is not drawn - which every hierarchy layout would place at the origin. */
      parentId: parent !== null && inScope.has(parent) ? parent : null,
      /**
       * Whatever the reader chose size to stand for - see `toNodeWeight`.
       *
       * The degree is the default and the conventional choice: in a graph, how connected something
       * is *is* the measure the picture is about. It is no longer the only one, because it is also
       * the measure a reader most often already knows.
       */
      weight: toWeight(componentId, degrees.get(componentId) ?? 0),
    };
  });

  return {
    viewInstanceId,
    graph: { nodes, edges },
    isGrouped: false,
    referenceIds,
    /* Nothing to collapse without a grouping rule. Containment between two loaded components is
       carried as `parentId` and laid out as a spring, not as a group - so there is no group node
       for a reader to close and nothing for the two header buttons to act on. */
    hasGroups: false,
    collapsibleGroupIds: [],
    foldedGroups: new Map(),
    /* The ungrouped path draws one mark per component and keys it on the component id, so the two
       are the same list. Stated rather than left implicit, since the grouped path's are not. */
    componentIds,
    numericFields,
    legend: {
      componentTypes: [...componentTypesByName.values()],
      referenceTypes: [...referenceTypesByName.values()],
      activeConditionalFormatting,
    },
    droppedReferenceCount,
    isViewEmpty: false,
    emptyReason: null,
    locale,
    i18nLocale,
  };
};
